import { Breakpoint, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

type TQuery = 'up' | 'down' | 'between' | 'only';

type TValue = Breakpoint | number;

export type TDeviceName = 'mobile' | 'tablet' | 'desktop';

type TReturnGetDevice = {
  device: TDeviceName;
  isDesktop: boolean;
  isTablet: boolean;
  isMobile: boolean;
  isXsMobile: boolean;
  isPortable: boolean;
  isSmallHeightDesktop: boolean;
  isSmallHeight: boolean;
  isLandscape: boolean;
  isPortrait: boolean;
  isMediumHeight: boolean;
  isXsDesktop: boolean;
};

export function useGetByBreakpoint(query: TQuery, start?: TValue, end?: TValue): boolean {
  const theme = useTheme();
  const mediaUp = useMediaQuery(theme.breakpoints.up(start as TValue));
  const mediaDown = useMediaQuery(theme.breakpoints.down(start as TValue));
  const mediaBetween = useMediaQuery(theme.breakpoints.between(start as TValue, end as TValue));
  const mediaOnly = useMediaQuery(theme.breakpoints.only(start as Breakpoint));

  if (query === 'up') {
    return mediaUp;
  }

  if (query === 'down') {
    return mediaDown;
  }

  if (query === 'between') {
    return mediaBetween;
  }

  return mediaOnly;
}

export const useGetByDevice = (): TReturnGetDevice => {
  const isDesktop = useGetByBreakpoint('up', 'lg');
  const isTablet = useGetByBreakpoint('between', 'sm', 'lg');
  const isMobile = useGetByBreakpoint('down', 'sm');
  const isXsMobile = useGetByBreakpoint('down', 376);
  const isXsDesktop = useGetByBreakpoint('between', 1024, 1400);

  const maxSmallSizeHeight = useMediaQuery('(max-height: 668px)');
  const maxMediumSizeHeight = useMediaQuery('(max-height: 800px)');

  const isPortable = isMobile || isTablet;
  const isSmallHeightDesktop = isDesktop && maxSmallSizeHeight;
  const device: TDeviceName = (() => {
    if (isMobile) {
      return 'mobile';
    }
    if (isTablet) {
      return 'tablet';
    }
    return 'desktop';
  })();

  const isLandscape = useMediaQuery('(orientation: landscape)');
  const isPortrait = useMediaQuery('(orientation: portrait)');

  return {
    device,
    isDesktop,
    isTablet,
    isMobile,
    isXsMobile,
    isXsDesktop,
    isPortable,
    isSmallHeightDesktop,
    isSmallHeight: maxSmallSizeHeight,
    isMediumHeight: maxMediumSizeHeight,
    isLandscape,
    isPortrait,
  };
};
